import React, { useState } from 'react';
import { Users, Copy, Check, Edit2, Share2, ChevronDown, ChevronUp } from 'lucide-react';
import { ref, update } from 'firebase/database';
import { database } from '../../firebase/config';

export default function BandSettings({ user, bandId, bandData, setError, showMessage }) {
  const [expanded, setExpanded] = useState(false);
  const [editingBandName, setEditingBandName] = useState(false);
  const [newBandName, setNewBandName] = useState('');
  const [copiedCode, setCopiedCode] = useState(false);

  const updateBandNameInDB = async () => {
    if (!newBandName.trim() || !bandId) return;
    try {
      await update(ref(database, `bands/${bandId}`), { name: newBandName });
      setEditingBandName(false);
      showMessage('Band name updated successfully!');
    } catch (error) {
      setError('Failed to update band name: ' + error.message);
    }
  };

  const copyInviteCode = () => {
    if (bandData?.inviteCode) {
      navigator.clipboard.writeText(bandData.inviteCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const shareInviteCode = async () => {
    if (!bandData?.inviteCode || !bandData?.name) return;
    const message = `Hi, I'm using Tapsody App (www.tapsody.app) to manage my band's songs and creative process. Join my band ${bandData.name} by using this Invite Code: ${bandData.inviteCode}`;

    if (navigator.share) {
      try {
        await navigator.share({ text: message });
      } catch (err) {
        if (err.name !== 'AbortError') {
          navigator.clipboard.writeText(message);
          showMessage('Message copied to clipboard!');
        }
      }
    } else {
      navigator.clipboard.writeText(message);
      showMessage('Invite message copied to clipboard!');
    }
  };

  return (
    <div className="card-3d rounded-card overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-white/5 transition text-left"
      >
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Users className="w-5 h-5 text-white" />
          Band Settings
        </h2>
        {expanded ? <ChevronUp className="w-5 h-5 text-white" /> : <ChevronDown className="w-5 h-5 text-white" />}
      </button>

      {expanded && (
        <div className="border-t border-surface-border-light p-4 sm:p-6">
          <div className="space-y-4">
            {/* Band Name */}
            <div>
              <label className="text-sm font-semibold mb-2 block">Band Name</label>
              {editingBandName ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={newBandName}
                    onChange={(e) => setNewBandName(e.target.value)}
                    className="w-full bg-transparent border border-surface-border-light px-4 py-2 text-white focus:outline-none focus:border-brand rounded-pill"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={updateBandNameInDB}
                      className="flex-1 bg-brand hover:bg-brand-hover transition h-btn font-semibold text-sm rounded-pill"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => { setEditingBandName(false); setNewBandName(''); }}
                      className="flex-1 bg-transparent border border-surface-border-light hover:border-surface-border transition h-btn font-semibold text-sm rounded-pill"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="flex-1 text-lg">{bandData?.name}</p>
                  <button
                    onClick={() => { setEditingBandName(true); setNewBandName(bandData?.name || ''); }}
                    className="bg-transparent hover:bg-brand transition p-2 rounded-full"
                  >
                    <Edit2 className="w-4 h-4 text-white" />
                  </button>
                </div>
              )}
            </div>

            {/* Invite Code */}
            <div>
              <label className="text-sm font-semibold mb-2 block">Invite Code</label>
              <div className="flex flex-col gap-2">
                <div className="bg-transparent border border-surface-border-light px-4 py-3 text-white font-mono text-base sm:text-lg flex items-center justify-center rounded-pill">
                  {bandData?.inviteCode}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={copyInviteCode}
                    className="flex-1 bg-brand hover:bg-brand-hover transition h-btn font-semibold flex items-center justify-center gap-2 text-sm rounded-pill"
                  >
                    {copiedCode ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copiedCode ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={shareInviteCode}
                    className="flex-1 bg-transparent border border-surface-border-light hover:bg-brand hover:border-brand transition h-btn font-semibold flex items-center justify-center gap-2 text-sm rounded-pill"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                </div>
              </div>
              <p className="text-xs text-text-subtle mt-2">Share this code with band members to invite them</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
